import { Suspense, useState } from 'react'
import { Link, Outlet, useLocation, Navigate } from 'react-router-dom'
import { MyTileDeskLockup } from '../components/MyTileDeskMark.jsx'
import { Button } from '../components/ui/index.jsx'
import {
  BillingIcon,
  CustomersIcon,
  DashboardIcon,
  ExpensesIcon,
  ImportIcon,
  InsightsIcon,
  InvoicesIcon,
  OutstandingIcon,
  PaymentIcon,
  ProductRequestIcon,
  PurchasesIcon,
  ReceiptsIcon,
  ReportsIcon,
  SettingsIcon,
  StockIcon,
  SuppliersIcon,
  TenantsIcon,
} from './NavIcons.jsx'
import RouteFallback from './RouteFallback.jsx'
import SideNav, { useNavPrefs } from './SideNav.jsx'
import ThemeToggle from '../features/theme/ThemeToggle.jsx'
import { useAuth, useCan } from '../lib/auth.jsx'
import { CAP } from '../lib/capabilities.js'
import { incompleteShopNotice, shopCannotInvoice } from '../lib/shop.js'
import './shell.css'

// 56px top bar, 232px left nav, filtered by the same capability list the server
// uses — so a hidden item and a 403 rarely disagree (§09a).
//
// The nav gate is a courtesy, not a control: /auth/me is read once at boot, so
// a cashier demoted mid-shift keeps the old menu until reload. The server is
// the control.

// Grouped under static category labels rather than the collapsible
// Selling/Buying pairs this replaced. Those two folded the buying and
// selling *workflows* together (a bill and the payment against it, a
// purchase and its supplier); this groups by what KIND of screen it is
// instead — a master record, a document, a payment — which is a different
// cut of the same nav and cannot keep both. A heading with no visible
// children (a role that can reach nothing under it) renders nothing, same
// guarantee the old collapsible groups made.
//
// Each section has an `id`: it is what a person's own arrangement of the rail
// (SideNav — the order, and which groups are closed) is remembered by, so a
// heading can be renamed without scrambling everybody's saved rail.
const NAV_SECTIONS = [
  {
    id: 'platform',
    heading: null,
    arrangeLabel: 'Support',
    items: [
      // Platform support gets one item and no business data. It is listed
      // first because for that role it is the only thing on the page.
      { to: '/platform/tenants', label: 'Tenants', cap: CAP.PLATFORM, icon: TenantsIcon },
    ],
  },
  {
    id: 'overview',
    heading: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', end: true, cap: null, tenantOnly: true, icon: DashboardIcon },
      // The charts behind the dashboard's numbers: this month against last,
      // the last 30 days or 12 months, where the money went. view_cost —
      // owner and manager — because it shows purchases and stock at cost.
      { to: '/insights', label: 'Insights', cap: CAP.VIEW_COST, icon: InsightsIcon },
    ],
  },
  {
    id: 'master',
    heading: 'Master Data',
    items: [
      { to: '/customers', label: 'Customers', cap: CAP.VIEW_CUSTOMERS, icon: CustomersIcon },
      { to: '/suppliers', label: 'Suppliers', cap: CAP.MANAGE_PARTIES, icon: SuppliersIcon },
      { to: '/products', label: 'Stock Details', cap: CAP.MANAGE_PRODUCTS, icon: StockIcon },
      // A want, not a document (V010). It is closed only from inside a
      // purchase, which is a Transactions-side fact, but it is READ from a
      // Master-Data-side question — "what do we need to get" — and that is
      // the question this rail answers by where it puts a link. Right
      // beside Products, where that question actually gets asked.
      {
        to: '/product-requests',
        label: 'Product requests',
        cap: CAP.PURCHASES,
        icon: ProductRequestIcon,
      },
    ],
  },
  {
    id: 'transactions',
    heading: 'Transactions',
    items: [
      { to: '/billing', label: 'Billing', cap: CAP.SELL, icon: BillingIcon },
      { to: '/invoices', label: 'Invoices', cap: CAP.SELL, icon: InvoicesIcon },
      // The same list as Customer Payments under Payments, on purpose: a
      // receipt is a document too, and this puts it beside the invoices it
      // settles. Both links light up on /receipts.
      { to: '/receipts', label: 'Receipts', cap: CAP.SELL, icon: ReceiptsIcon },
      { to: '/purchases', label: 'Purchases', cap: CAP.PURCHASES, icon: PurchasesIcon },
      // Money out that is not stock (V020): transport, hamali, tea, rent.
      // Every tenant role — the hamali is paid at the counter.
      { to: '/expenses', label: 'Expenses', cap: CAP.RECORD_EXPENSES, icon: ExpensesIcon },
    ],
  },
  {
    id: 'payments',
    heading: 'Payments',
    items: [
      // Taking money in, against no particular bill — a deposit or an
      // advance, which in this trade is the normal way a large tile order
      // starts. `sell`, because both routes behind it (POST /api/receipts
      // and /api/receipts/{id}/allocations) are CanSell on the server.
      //
      // Points at the list (receipts), same as Supplier Payments below points
      // at its own list rather than straight at "record an advance" — the
      // form is one click away from either, on the button that opens it.
      { to: '/receipts', label: 'Customer Payments', cap: CAP.SELL, icon: PaymentIcon },
      {
        to: '/supplier-payments',
        label: 'Supplier Payments',
        cap: CAP.PURCHASES,
        icon: PaymentIcon,
      },
      // view_customers, not sell: the report behind this screen is
      // CanViewCustomers, and taking money is gated inside the screen
      // itself. Same outcome for every role in the matrix today — both
      // capabilities are held together or not at all.
      { to: '/outstanding', label: 'Outstanding', cap: CAP.VIEW_CUSTOMERS, icon: OutstandingIcon },
    ],
  },
  {
    id: 'tools',
    heading: null,
    // No heading on the rail — it is the shop-wide tools at the foot — but it
    // still needs a name while sections are being moved.
    arrangeLabel: 'Tools',
    items: [
      // The three first-day files (V015): suppliers, products with opening
      // stock, customers. Here with the shop-wide tools rather than among the
      // masters, because it is something done once, not a list visited daily.
      // Owner only; a platform super user sees it while impersonating the
      // owner. '/imports' rather than one tab, so it stays lit on all three.
      { to: '/imports', label: 'Bulk import', cap: CAP.BULK_IMPORT, icon: ImportIcon },
      { to: '/reports', label: 'Reports', cap: null, tenantOnly: true, icon: ReportsIcon },
      { to: '/settings', label: 'Settings', cap: CAP.SETTINGS, icon: SettingsIcon },
    ],
  },
]

/** Billing is blocked until Settings is finished. Says so on every screen.
 *
 * Persistent rather than a toast on the billing page: the person who can fix it
 * is the owner, who may never open Billing, and the person who discovers it is
 * the cashier, who cannot. A cashier is told what is wrong and who fixes it,
 * never sent to a screen they will be refused at (§09a).
 */
function IncompleteShopBanner({ tenant, canFix }) {
  return (
    <div className="shell-incomplete" role="alert">
      <span>{incompleteShopNotice(tenant)}</span>
      {canFix ? (
        <Link className="shell-incomplete-link" to="/settings">
          Finish shop details
        </Link>
      ) : (
        <span>Ask the shop owner to finish the shop details in Settings.</span>
      )}
    </div>
  )
}

/** Who came in, for the impersonation banner: "Bhanu <bhanu@…>", or null.
 *
 * Read from `tp_impersonator`, which the impersonation route sets deliberately
 * readable — it carries a name, not a session. The two cookies that are
 * credentials sit beside it and stay httpOnly, so nothing here can be used to
 * sign in as anybody. Falls back to null rather than throwing on a value that
 * did not survive encoding; the banner has a generic wording for that.
 */
function impersonatorLabel() {
  if (typeof document === 'undefined') return null
  const found = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith('tp_impersonator='))
  if (!found) return null
  try {
    return decodeURIComponent(found.slice('tp_impersonator='.length)) || null
  } catch {
    return null
  }
}

export function RequireAuth({ children }) {
  const { status } = useAuth()
  const location = useLocation()

  if (status === 'loading') return <Splash />
  if (status === 'offline') return <OfflineNotice />
  if (status !== 'authenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }
  return children
}

export function RequireCapability({ capability, children }) {
  const can = useCan()
  // Fails closed while /auth/me is unresolved, because useCan() does.
  if (!can(capability)) {
    return (
      <div className="shell-denied">
        <h2>You do not have access to this</h2>
        <p>Ask the shop owner if you need it.</p>
      </div>
    )
  }
  return children
}

/** Whether this rail entry is shown at all. The gate is on the route this
 *  link points at, never on the heading grouping it — a section heading has
 *  no capability of its own; it is exactly as visible as the items under it. */
function visible(item, can, tenant) {
  if (item.tenantOnly && !tenant) return false
  return item.cap === null || can(item.cap)
}

/** What is on screen while /auth/me is unresolved. Exported because `/` now
 *  serves two different pages depending on the answer, and routes.jsx needs the
 *  same holding screen rather than a second one — see the note there. */

export function Splash() {
  return (
    <div className="shell-splash" role="status" aria-live="polite">
      <MyTileDeskLockup markSize={40} size="md" />
      <span className="visually-hidden">Loading</span>
    </div>
  )
}

function OfflineNotice() {
  const { reload } = useAuth()
  return (
    <div className="shell-splash">
      <MyTileDeskLockup markSize={40} size="md" />
      <p className="shell-offline">
        You are offline. Billing needs a connection because stock has to be reserved as the
        bill is posted.
      </p>
      <Button variant="primary" onClick={reload}>
        Try again
      </Button>
    </div>
  )
}

export default function AppShell() {
  const { me, signOut } = useAuth()
  const can = useCan()
  const tenant = me?.tenant
  const location = useLocation()
  // Read once, at mount: the sign-in that brought the owner here was their
  // first since the platform approved the shop (V013). Kept in state rather
  // than read from the location each render, so the banner stays while they
  // move between screens and goes when they dismiss it or reload.
  const [approvedWelcome, setApprovedWelcome] = useState(
    () => location.state?.approvedWelcome === true,
  )
  // This login's own rail: folded or open, its order, its closed groups.
  const [navPrefs, setNavPrefs] = useNavPrefs(me?.user_id)

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      {/* The impersonation banner is full width, above everything, at z-index 70.
          A vendor who can enter your books invisibly is a vendor you would not
          choose — so the shop owner sees this too, in their activity log.

          It names both parties: the support login that came in, and the shop
          login it came in as. `me.impersonated_by` is a uuid — this session
          cannot resolve it, because reading a platform user is a platform
          capability and this is an ordinary tenant session — so the name comes
          from the readable cookie POST /api/platform/tenants/{id}/impersonate
          sets beside the two httpOnly ones. It is a label and not a credential.

          Rendered only when `me.impersonated_by` is set, which is what makes a
          stale cookie harmless: it names nobody on a screen that is not shown. */}
      {me?.impersonated_by ? (
        <div className="shell-impersonation" role="alert">
          {impersonatorLabel() ?? 'Platform support'} is signed in as {me.full_name} at{' '}
          {tenant?.business_name}. Every action is recorded in this shop's activity log.
        </div>
      ) : null}

      {/* The one banner that is good news, and the only one that can be
          dismissed: it marks an arrival rather than a state anybody has to
          act on. Above the incomplete-shop banner, which a newly approved
          shop will usually see too — first the welcome, then what to do. */}
      {approvedWelcome && tenant ? (
        <div className="shell-welcome" role="status">
          <span>{tenant.business_name} is approved — welcome to myTileDesk.</span>
          <button
            type="button"
            className="shell-welcome-dismiss"
            onClick={() => setApprovedWelcome(false)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {tenant && tenant.is_active === false ? (
        <div className="shell-suspended" role="alert">
          This account is suspended. You can still read everything and print invoices, but
          new bills cannot be posted.
        </div>
      ) : null}

      {/* The shop's own record has to be complete before a tax invoice can be
          printed: no GSTIN and no address means the buyer cannot claim input
          credit, and Rule 5 means the bill can never be corrected afterwards.
          The server refuses to post one and says so on /auth/me. */}
      {shopCannotInvoice(tenant) ? (
        <IncompleteShopBanner tenant={tenant} canFix={can(CAP.SETTINGS)} />
      ) : null}

      <header className="shell-top">
        <MyTileDeskLockup markSize={28} size="sm" />
        <div className="shell-top-right">
          <span className="shell-who">
            <span className="shell-who-name">{me?.full_name}</span>
            {/* A platform user belongs to no shop. Saying so beats rendering an
                empty line where every other user sees their business name. */}
            <span className="shell-who-shop">
              {tenant?.business_name ?? 'Platform support'}
            </span>
          </span>
          {/* A shop preference, not an account setting: it belongs to this
              machine, so it sits in the bar rather than behind Settings, where
              a cashier without the settings capability could never reach it. */}
          <ThemeToggle />
          {/* The full class list, not just the size. <Button> spreads `rest`
              after its own className, so passing one replaces "ui-btn
              ui-btn-quiet" instead of adding to it — and the button falls back
              to the browser's own grey, which follows neither palette. */}
          <Button
            variant="quiet"
            className="ui-btn ui-btn-quiet ui-btn-sm"
            onClick={signOut}
          >
            Sign out
          </Button>
        </div>
      </header>

      <div className={navPrefs.collapsed ? 'shell-body is-nav-collapsed' : 'shell-body'}>
        <SideNav
          sections={NAV_SECTIONS}
          isVisible={(item) => visible(item, can, tenant)}
          prefs={navPrefs}
          onPrefs={setNavPrefs}
        />

        <main className="shell-main" id="main">
          {/* Every screen but Today is a separate chunk (see routes.jsx), and
              the boundary belongs here rather than around the shell: wrapped
              one level up it would unmount the top bar and the nav on every
              navigation, so the chrome would blink out and back on each click.
              Here the nav stays put, keeps its active item, and only the page
              body is replaced by the skeleton. */}
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  )
}
